import { DatePipe } from "@angular/common";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    inject
} from "@angular/core";
import { TranslateDirective } from "@ngx-translate/core";
import { isNil } from "lodash";
import { BehaviorSubject, Subject } from "rxjs";
import { filter, map, takeUntil } from "rxjs/operators";
import { Arbetsordertyper, VirkesorderStatus } from "~app/common/models/constants";
import { EventService } from "~app/common/services/cqs/event.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { VirkesorderStatusChangedEventMessage } from "~app/features/planera/events/VirkesorderStatusChangedEventMessage";
import { IArbetsorder } from "~app/features/planera/model/IArbetsorder";
import { ArbetsorderEventStore } from "~app/features/shared/services/arbetsorderEventStore.service";
import { ArbetsorderStatusIcons, IArbetsorderStatusIcons } from "../arbetsorder-planering-scheduler/arbetsorder-status-ikoner/arbetsorder-status-ikoner-models";
import { ArbetsorderStatusIkonerComponent } from "../arbetsorder-planering-scheduler/arbetsorder-status-ikoner/arbetsorder-status-ikoner.component";
import { ArbetsorderWarningPillComponent } from "../arbetsorder-warning-pill/arbetsorder-warning-pill.component";
import { ArbetsorderAttributEventMessage, ArbetsorderAttributGrupper } from "../events/ArbetsorderAttributEventMessage";
import { ArbetsorderMaskinlagUpdatedMessage } from "../events/ArbetsorderMaskinlagUpdatedMessage";
import { ArbetsorderNamnUpdatedMessage } from "../events/ArbetsorderNamnUpdatedMessage";
import { ArbetsorderOperationDeletedEvent } from "../events/ArbetsorderOperationDeletedEvent";
import { ArbetsorderOperationUpdatedEvent } from "../events/ArbetsorderOperationUpdatedEvent";
import { DestineringsalternativMergeCompletedEventMessage } from "../events/DestineringsalternativMergeCompletedEventMessage";
import { IArbetsorderAtgarder } from "../model/IArbetsorderAtgarder";
import { ArbetsorderVirkesorderService } from "../services/arbetsorder-virkesorder.service";
import { ArbetsorderService } from "../services/arbetsorder.service";
import { ArbetsorderHeaderPresentationRulesModel } from "./ArbetsorderHeaderPresentationRulesModel";
import { ArbetsorderHeaderLisRowTexts } from "./arbetsorder-header-list-row.texts";

@Component({
    selector: "rask-arbetsorder-header-list-row",
    styleUrls: ["./arbetsorder-header-list-row.component.scss"],
    templateUrl: "./arbetsorder-header-list-row.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        TranslateDirective,
        ArbetsorderWarningPillComponent,
        ArbetsorderStatusIkonerComponent,
        DatePipe
    ]
})
export class ArbetsorderHeaderListRowComponent implements OnChanges, OnInit, OnDestroy {

    aktuellVirkesorderStatus?: string;
    @Input() arbetsorder: IArbetsorderAtgarder;
    arbetsorderStatusIcons: IArbetsorderStatusIcons;
    presentationRules: ArbetsorderHeaderPresentationRulesModel;
    showSpinner$ = new BehaviorSubject<boolean>(false);
    showViol3Spinner$ = new BehaviorSubject<boolean>(false);
    skickadTillViol: boolean;
    texts = ArbetsorderHeaderLisRowTexts.texts;
    viol3Icon$ = new BehaviorSubject<string>("initial");
    virkeskopIcon$ = new BehaviorSubject<string>("initial");
    voContractDirty: boolean;

    private readonly arbetsorderEventStore = inject(ArbetsorderEventStore);
    private readonly arbetsorderService = inject(ArbetsorderService);
    private readonly arbetsorderVirkesorderService = inject(ArbetsorderVirkesorderService);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly eventService = inject(EventService);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly zone = inject(NgZone);

    private dirtyProduktion: boolean;
    private dirtyTransport: boolean;
    private hasTransmissionErrorViol3: boolean;
    private skickadTillViol3: boolean;

    formatFordelning = (value: number) => {
        if (!ObjectUtils.hasValue(value)) {
            return "";
        }
        return `${value}%`;
    };

    getArbetsorderPeriod(arbetsorder: IArbetsorder) {
        if (!arbetsorder) {
            return "";
        }

        return this.arbetsorderService.getArbetsorderOperationPeriod(
            arbetsorder
        );
    }

    hasExtraordinarHandelse() {
        return this.arbetsorder.atgarder
            .some(atgard => atgard.extraordinarHandelse?.length > 0);
    }
    isSkordarmatning() {
        return this.arbetsorder.skordarmatning;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (
            changes.arbetsorder &&
            changes.arbetsorder.currentValue !== undefined
        ) {
            const ao = changes.arbetsorder;
            this.arbetsorder = ao.currentValue;
            this.presentationRules = new ArbetsorderHeaderPresentationRulesModel(
                this.arbetsorder
            );

            this.arbetsorderStatusIcons = ArbetsorderStatusIcons.fromArbetsorderFactory(this.arbetsorder, this.presentationRules);
            this.setupEvents();
        }
    }

    ngOnDestroy(): void {
        this.arbetsorderEventStore.removeArbetsorderList([this.arbetsorder.id]);
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit() {
        this.setupEvents();

        this.arbetsorderEventStore.arbetsorderStoreEventsLoaded$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .pipe(filter(x => x.has(this.arbetsorder.id)), map(z => z.get(this.arbetsorder.id)))
            .subscribe(x => {
                this.dirtyProduktion = x.produktionsdelIsDirty;
                this.dirtyTransport = x.transportdelIsDirty;
                this.hasTransmissionErrorViol3 = x.hasTransmissionErrorViol3;
                this.skickadTillViol3 = ObjectUtils.hasValue(x.senastSkickat);
                this.showViol3Spinner$.next(x.arbetsorderIsSending);
                this.setIkonForViol3();
            });
        this.arbetsorderEventStore.addArbetsorderList([this.arbetsorder.id]);

        this.arbetsorderEventStore.arbetsorderSendStatusChanged$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .pipe(filter(x => x.arbetsorderId === this.arbetsorder.id))
            .subscribe(x => {
                this.showViol3Spinner$.next(x.arbetsorderIsSending);
                this.triggerDigestCycle();
            });

        this.arbetsorderEventStore.arbetsorderSynchFinished$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .pipe(filter(x => x.arbetsorderId === this.arbetsorder.id))
            .subscribe(x => {
                this.dirtyProduktion = x.produktionsdelIsDirty;
                this.dirtyTransport = x.transportdelIsDirty;
                this.hasTransmissionErrorViol3 = x.hasTransmissionErrorViol3;
                this.skickadTillViol3 = ObjectUtils.hasValue(x.senastSkickat);
                this.setIkonForViol3();
            });
    }

    subscribeVirkesorderEvent(
        nyckel: string,
        cb: (event: ArbetsorderAttributEventMessage) => void
    ) {
        this.eventService
            .subscribeEvent<ArbetsorderAttributEventMessage>(
                ArbetsorderAttributEventMessage.$type
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .pipe(
                filter(
                    (event) =>
                        event.arbetsorderId === this.arbetsorder.id &&
                        event.grupp ===
                        ArbetsorderAttributGrupper.VirkesorderValidation &&
                        event.nyckel === nyckel
                )
            )
            .subscribe(cb);
    }

    protected isGrot(): boolean {
        return this.arbetsorder.typ === Arbetsordertyper.Grotskotning;
    }

    private setIkonForViol3(): void {
        const ikon = this.arbetsorderVirkesorderService.getIkonForViol3(
            this.hasTransmissionErrorViol3,
            this.skickadTillViol3,
            this.dirtyProduktion,
            this.dirtyTransport);
        this.viol3Icon$.next(ikon);
        this.triggerDigestCycle();
    }

    private setIkonVirkesorderstatus(): void {
        if (this.aktuellVirkesorderStatus === VirkesorderStatus.Sand) {
            this.showSpinner$.next(true);
        } else {
            const ikon = this.arbetsorderVirkesorderService.getIkonVirkesorderstatus(this.aktuellVirkesorderStatus, this.voContractDirty, this.skickadTillViol);
            if (!isNil(ikon)) {
                this.virkeskopIcon$.next(ikon);
                this.showSpinner$.next(false);
            }
        }
        this.triggerDigestCycle();
    }

    private setupEvents() {
        this.aktuellVirkesorderStatus = this.arbetsorder.aktuellVirkesorderStatus;
        this.skickadTillViol = this.arbetsorder.skickadTillViol;
        this.voContractDirty = this.arbetsorder.voContractDirty;

        this.setIkonVirkesorderstatus();

        this.dirtyProduktion = this.arbetsorder.dirtyProduktionsdel;
        this.dirtyTransport = this.arbetsorder.dirtyTransportdel;
        this.hasTransmissionErrorViol3 = this.arbetsorder.hasTransmissionErrorProduktionViol3 || this.arbetsorder.hasTransmissionErrorTransportViol3;
        this.skickadTillViol3 = this.arbetsorder.skickadTillViol3;

        this.presentationRules = new ArbetsorderHeaderPresentationRulesModel(
            this.arbetsorder
        );

        this.eventService
            .subscribeEvent<DestineringsalternativMergeCompletedEventMessage>(
                DestineringsalternativMergeCompletedEventMessage.$type
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .pipe(
                filter((event) => event.arbetsorderId === this.arbetsorder.id)
            )
            .subscribe((event) => {
                this.arbetsorder.destinerad = event.destinerad;
                this.changeDetectorRef.markForCheck();
            });

        this.eventService
            .subscribeEvent<ArbetsorderOperationUpdatedEvent>(
                ArbetsorderOperationUpdatedEvent.$type
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .pipe(
                filter((event) => event.arbetsorderId === this.arbetsorder.id)
            )
            .subscribe(() => {
                this.changeDetectorRef.markForCheck();
            });

        this.eventService
            .subscribeEvent<ArbetsorderOperationDeletedEvent>(
                ArbetsorderOperationUpdatedEvent.$type
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .pipe(
                filter((event) => event.arbetsorderId === this.arbetsorder.id)
            )
            .subscribe(() => {
                this.changeDetectorRef.markForCheck();
            });

        this.eventService
            .subscribeEvent<ArbetsorderNamnUpdatedMessage>(
                ArbetsorderNamnUpdatedMessage.$type
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .pipe(filter((event) => event.id === this.arbetsorder.id))
            .subscribe(() => {
                this.changeDetectorRef.markForCheck();
            });
        this.setIkonForViol3();

        this.eventService.subscribeEvent<VirkesorderStatusChangedEventMessage>(VirkesorderStatusChangedEventMessage.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .pipe(filter(event => event.arbetsorderId === this.arbetsorder.id))
            .subscribe((event) => {
                this.aktuellVirkesorderStatus = event.aktuellVirkesorderStatus;
                this.skickadTillViol = !ObjectUtils.isNullOrUndefined(event.senastAckadGeneration);
                this.voContractDirty = event.isDirty;
                this.setIkonVirkesorderstatus();
            });

        this.eventService
            .subscribeEvent<ArbetsorderMaskinlagUpdatedMessage>(
                ArbetsorderMaskinlagUpdatedMessage.$type
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .pipe(
                filter((event) => event.arbetsorderId === this.arbetsorder.id)
            )
            .subscribe(() => {
                this.changeDetectorRef.markForCheck();
            });
    }

    private triggerDigestCycle(): void {
        this.zone.run(() => {
            // Tvinga körning av digest
        });
    }
}
